import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

import { useAddAttributeOption, useUpdateAttributeOption } from '../../hooks';
import type { AttributeOption, CatalogLanguage } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';
import { LocalizedFieldsEditor } from './LocalizedFieldsEditor';
import type { LocalizedFieldValue } from './LocalizedFieldsEditor';

interface AttributeOptionFormDialogProps {
  open: boolean;
  attributeCode: string;
  /** The option being edited, or `null` to add a new one. */
  editing: AttributeOption | null;
  languages: CatalogLanguage[];
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/** Add / edit a SELECT or MULTISELECT attribute's option — code, sort order, active, label. */
export function AttributeOptionFormDialog({
  open,
  attributeCode,
  editing,
  languages,
  onClose,
  onResult,
}: AttributeOptionFormDialogProps) {
  const { t } = useTranslation('catalog');
  const addOption = useAddAttributeOption();
  const updateOption = useUpdateAttributeOption();
  const isEdit = editing !== null;
  const pending = addOption.isPending || updateOption.isPending;

  const [code, setCode] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [active, setActive] = useState(true);
  const [labelValues, setLabelValues] = useState<LocalizedFieldValue[]>([]);

  useEffect(() => {
    if (!open) return;
    setCode(editing?.code ?? '');
    setSortOrder(editing?.sortOrder !== undefined ? String(editing.sortOrder) : '0');
    setActive(editing?.active ?? true);
    setLabelValues(
      (editing?.translations ?? []).map((translation) => ({
        locale: translation.locale,
        text: translation.label,
      })),
    );
  }, [open, editing]);

  const valid = code.trim() !== '';

  const submit = () => {
    if (!valid) return;
    const translations = labelValues.map((v) => ({ locale: v.locale, label: v.text }));
    const parsedSortOrder = sortOrder.trim() === '' ? undefined : Number(sortOrder);
    const handlers = {
      onSuccess: () => {
        onResult(
          'success',
          isEdit ? t('attributeOptionFormDialog.saved') : t('attributeOptionFormDialog.created'),
        );
        onClose();
      },
      onError: (error: { message: string }) => onResult('error', error.message),
    };
    if (isEdit && editing) {
      updateOption.mutate(
        {
          code: attributeCode,
          optionCode: editing.code,
          body: { sortOrder: parsedSortOrder, active, translations },
        },
        handlers,
      );
      return;
    }
    addOption.mutate(
      { code: attributeCode, body: { code: code.trim(), sortOrder: parsedSortOrder, translations } },
      handlers,
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {isEdit ? t('attributeOptionFormDialog.editTitle') : t('attributeOptionFormDialog.newTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label={t('attributeOptionFormDialog.codeLabel')}
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            error={code.trim() === ''}
            disabled={isEdit}
            fullWidth
            slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
          />
          <TextField
            label={t('attributeOptionFormDialog.sortOrderLabel')}
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            fullWidth
            slotProps={{ htmlInput: { step: 1 } }}
          />
          {isEdit ? (
            <FormControlLabel
              control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
              label={t('attributeOptionFormDialog.activeLabel')}
            />
          ) : null}
          <LocalizedFieldsEditor
            languages={languages}
            values={labelValues}
            onChange={setLabelValues}
            fieldLabel={t('attributeOptionFormDialog.labelFieldLabel')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || pending}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
